import withRouter from "components/Common/withRouter";
import PropTypes from "prop-types";
import React, { useState, useRef, useEffect } from "react";
import {
  Row,
  Col,
  Input,
  Form,
  FormGroup,
  Label,
  Spinner,
} from "reactstrap";
import logoImg from "../../assets/images/logo.png";
import * as Yup from "yup";
import { formatDataBeforeSend } from "helpers/GeneralHelper";
import { passwordRequest } from "model/LoginModel";
import { toast } from "react-toastify";
import { useUserContext } from "libs/context/UserContext";
import { useNavigate } from "react-router-dom";

const Login = () => {
  const formatOptions = [];
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [code, setCode] = useState(Array(6).fill(""));
  const inputsRef = useRef([]);
  const [showVerificationCodeInputValue, setShowVerificationCodeInput] = useState(false);
  const [timer, setTimer] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const { loginUser } = useUserContext() || {};
  const navigate = useNavigate();

  // ✅ Yup email validation schema
  const emailSchema = Yup.string()
    .email("Invalid email address")
    .required("Email is required");

  const validateEmail = async (val) => {
    try {
      await emailSchema.validate(val);
      setEmailError("");
      return true;
    } catch (err) {
      setEmailError(err?.message);
      return false;
    }
  };

  const handleEmailChange = async (e) => {
    const val = e?.target?.value;
    setEmail(val);
    await validateEmail(val);
  };

  // ✅ Handle verification code input
  const handleChange = (val, idx) => {
    const newCode = [...code];

    if (val.length > 1) {
      const chars = val.split("").slice(0, 6 - idx);
      chars.forEach((c, i) => {
        newCode[idx + i] = c.toUpperCase();
      });
      setCode(newCode);

      if (newCode.every((d) => d !== "")) {
        // Optional: auto-submit if complete
      } else {
        inputsRef.current[Math.min(idx + chars.length, 5)]?.focus();
      }
      return;
    }

    newCode[idx] = val.toUpperCase();
    setCode(newCode);

    if (newCode.every((d) => d !== "")) {
      // Optional: auto-submit if complete
    } else if (val && idx < 5) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (e, idx) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").trim();
    if (!pasted) return;

    const chars = pasted.split("").slice(0, 6 - idx);
    const newCode = [...code];

    chars.forEach((c, i) => {
      newCode[idx + i] = c.toUpperCase();
    });

    setCode(newCode);
    inputsRef.current[Math.min(idx + chars.length, 5)]?.focus();
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !code[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };

  // ✅ Handle Enter key globally
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!showVerificationCodeInputValue) {
        if (isFormValidForEmail && !isLoading) {
          showVerificationCodeInput();
        }
      } else {
        if (isFormValidForCodeAndEmail && !isLoading) {
          loginFinalSubmittion(e);
        }
      }
    }
  };

  const loginFinalSubmittion = (e) => {
    e.preventDefault();
    const inputFields = {};
    const formData = formatDataBeforeSend(inputFields, formatOptions);
    formData.append("Email", email);
    formData.append("Password", code.join(""));
    loginUser(formData);
  };

  const handleResend = () => {
    if (timer > 0) return;
    setTimer(300);
    resendCodeInput();
  };

  const showVerificationCodeInput = async () => {
    if (await validateEmail(email)) {
      try {
        setLoading(true);
        let response = await passwordRequest({ Email: email });
        if (response?.IsSuccess) {
          setShowVerificationCodeInput(true);
          setTimer(300);
        } else {
          toast.error(response?.Message);
        }
      } catch (error) {
        toast.error("Something went wrong, please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  const resendCodeInput = async () => {
    if (await validateEmail(email)) {
      try {
        setLoading(true);
        let response = await passwordRequest({ Email: email });
        if (response?.IsSuccess) {
          toast.success(response?.Message);
        } else {
          toast.error(response?.Message);
        }
      } catch (error) {
        toast.error("Something went wrong, please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  // ✅ Timer countdown — depend on active state, not every tick
  const isTimerActive = timer > 0;
  useEffect(() => {
    if (!isTimerActive) return;
    const t = setInterval(() => {
      setTimer((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [isTimerActive]);

  // ✅ Redirect if already logged in
  useEffect(() => {
    const authUser = localStorage.getItem("authUser");
    if (authUser) {
      navigate(`/Home`);
    }
  }, [navigate]);

  const isCodeComplete = code.every((d) => d !== "");
  const isFormValidForEmail = email && !emailError;
  const isFormValidForCodeAndEmail = isFormValidForEmail && isCodeComplete;

  return (
    <div
      className={`d-flex w-100 ${isMobile ? "" : "vh-100"} align-items-center justify-content-center ${
        isMobile ? "bg-white" : "bg-cream"
      }`}
    >
      <Row className="w-100 justify-content-center">
        <Col md={4} className={`p-4 ${isMobile ? "bg-white shadow rounded" : "bg-cream"}`}>
          <div className="text-center mb-4">
            <img src={logoImg} alt="Funnl" />
          </div>

          <h4 className="fw-bold mb-3">
            Log In to your <br /> account
          </h4>

          {/* ✅ Form captures Enter key */}
          <Form onKeyDown={handleKeyPress}>
            {/* Email Input */}
            <div className="col-md-12">
              <div className={!showVerificationCodeInputValue ? "form-floating" : ""}>
                <input
                  type="text"
                  className="form-control login-input-background text-fnt-sz"
                  name="email"
                  placeholder="Enter Email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={(e) => validateEmail(e?.target?.value)}
                  disabled={showVerificationCodeInputValue}
                />
                {!showVerificationCodeInputValue && (
                  <label className="login-label">Enter Your Email</label>
                )}
              </div>
              {emailError && <div className="text-danger small error-msg-pos">{emailError}</div>}
            </div>

            {!showVerificationCodeInputValue && (
              <>
                <div className="d-flex justify-content-center mt-4">
                  <div className="col-auto w-100-percent">
                    <button
                      type="button"
                      className={`btn btn-primary-2 ${
                        !isFormValidForEmail ? "login-button-color-for-disabled" : "login-button-color"
                      } w-100-percent`}
                      disabled={!isFormValidForEmail || isLoading}
                      onClick={showVerificationCodeInput}
                    >
                      {isLoading ? (
                        <>
                          <Spinner size="sm" className="me-2" /> Loading...
                        </>
                      ) : (
                        "Continue"
                      )}
                    </button>
                  </div>
                </div>
                <p className="text-center mt-3 mb-0">
                  We will send you a code for a password-free process
                </p>
              </>
            )}

            {showVerificationCodeInputValue && (
              <>
                <FormGroup className="mt-3">
                  <Label>Enter the code</Label>
                  <div className="d-flex justify-content-between">
                    {code?.map((digit, i) => (
                      <Input
                        key={i}
                        type="text"
                        maxLength="1"
                        innerRef={(el) => (inputsRef.current[i] = el)}
                        value={digit}
                        onChange={(e) => handleChange(e.target.value, i)}
                        onKeyDown={(e) => handleKeyDown(e, i)}
                        onPaste={(e) => handlePaste(e, i)}
                        className="text-center mx-1 verification-code"
                        autoFocus={i === 0}
                      />
                    ))}
                  </div>
                </FormGroup>

                <div className="d-flex justify-content-center mt-4">
                  <div className="col-auto w-100-percent">
                    <button
                      type="button"
                      className={`btn btn-primary-2 w-100-percent ${
                        !isFormValidForCodeAndEmail
                          ? "login-button-color-for-disabled"
                          : "login-button-color"
                      }`}
                      disabled={!isFormValidForCodeAndEmail || isLoading}
                      onClick={(e) => loginFinalSubmittion(e)}
                    >
                      {isLoading ? (
                        <>
                          <Spinner size="sm" className="me-2" /> Logging in...
                        </>
                      ) : (
                        "Log In"
                      )}
                    </button>
                  </div>
                </div>

                <p className="text-center mt-3 mb-0">
                  Have not received any code?{" "}
                  <span
                    className="text-primary cursor-pointer"
                    onClick={handleResend}
                  >
                    {timer > 0
                      ? `${Math.floor(timer / 60)}:${String(timer % 60).padStart(2, "0")}`
                      : "Resend"}
                  </span>
                </p>
              </>
            )}
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default withRouter(Login);

Login.propTypes = {
  history: PropTypes.object,
};
